import { motion } from "framer-motion";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { NavBarConnect } from "./NavBarConnect";
import { LogoVariant } from "./Icons/LogoVariant";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRightIcon } from "./Icons/ArrowRightIcon";
// import { SublinkIcon } from "./Icons/SublinkIcon";
// import { toast } from "react-toastify";
import { cn } from "../utils/cn";
import { Logo } from "./Icons/Logo";
import { LogoIcon } from "./Icons/LogoIcon";
import {
  ArrowUpDown,
  Book,
  CandlestickChart,
  ChevronRight,
  Circle,
  CreditCard,
  HelpCircle,
  MessageCircle,
  ScanLine,
  ShieldCheck,
  Spade,
} from "lucide-react";
import { ANDROID_LINK, TESTFLIGHT_LINK } from "../constants";
import { AppleIcon } from "./Icons/AppleIcon";
import { AndroidIcon } from "./Icons/AndroidIcon";

const SCROLL_HEIGHT = 64;

enum PanelVariant {
  DEFAULT = "default",
  EQUAL_SPLIT = "equal-split",
}

interface SubLinkType {
  name: string;
  href: string;
  icon: React.ReactNode;
  comingSoon?: boolean;
  description?: string;
}

interface LinkType {
  name: string;
  href: string;
  description?: string;
  subLinksTitle?: string;
  subLinks?: SubLinkType[];
  panelVariant?: PanelVariant;
  leftPanel?: React.ReactNode;
}

enum LinkEnum {
  HOME = "Home",
  TRADE = "Trade",
  SWAP = "Swap",
  FUTURES = "Futures",
  SPOT = "Spot",
  AFFILIATE = "Affiliate",
  AFFILIATE_LANDING = "Landing",
  AFFILIATE_PORTAL = "Portal",
  GOLD = "Gold+",
  SECURITY = "Security",
  BLOG = "Blog",
  FAQ = "FAQ",
  CARDS = "Cards",
  ALL_CARDS = "All Cards",
  RESOURCES = "Resources",
}

const LINKS: LinkType[] = [
  { name: LinkEnum.HOME, href: "https://basedapp.io" },
  {
    name: LinkEnum.TRADE,
    href: "#",
    description:
      "Trade on the best mobile trading app for Hyperliquid, built and fine-tuned for top traders.",
    panelVariant: PanelVariant.EQUAL_SPLIT,
    subLinksTitle: "Web Trading",
    subLinks: [
      {
        name: LinkEnum.FUTURES,
        href: "https://basedapp.io/futures",
        icon: <CandlestickChart className="w-4 h-4 text-primary" />,
      },
      {
        name: LinkEnum.SPOT,
        href: "https://basedapp.io/spot",
        icon: <ScanLine className="w-4 h-4 text-primary" />,
      },
      {
        name: LinkEnum.SWAP,
        href: "https://basedapp.io/swap",
        icon: <ArrowUpDown className="w-4 h-4 text-primary" />,
      },
    ],
    leftPanel: (
      <div className="px-4 flex flex-col gap-y-2 pt-4 pb-6 col-span-1 border-r border-white/10">
        <div className="px-2 pt-2 font-display text-xs uppercase tracking-widest mt-1 text-primary drop-shadow-primary-3px">
          Mobile Trading
        </div>
        <Link
          to={TESTFLIGHT_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="group cursor-pointer flex items-center gap-x-4 overflow-hidden rounded-xl py-2 px-2 relative isolate">
          <div className="w-[0px] group-hover:w-full h-full bg-gradient-to-r group-hover:from-transparent group-hover:to-primary/15 absolute duration-200 transition-[width] right-0 top-0" />
          <div className="shrink-0 w-10 h-10 rounded-lg bg-gradient-to-b from-white/10 to-primary/10 flex items-center justify-center drop-shadow-primary-2px hover:drop-shadow-primary-8px duration-200">
            <AppleIcon className="h-4 text-primary" />
          </div>
          <div>
            <div className="text-sm">Apple iOS</div>
            <div className="text-xs text-white/60 -mt-1.5">
              Testflight Open Beta
            </div>
          </div>
          <div className="ml-auto pr-2 group-hover:text-primary duration-200 group-hover:-mr-1">
            <ChevronRight className="w-4 h-4" />
          </div>
        </Link>
        <Link
          to={ANDROID_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="group cursor-pointer flex items-center gap-x-4 overflow-hidden rounded-xl py-2 px-2 relative isolate">
          <div className="w-[0px] group-hover:w-full h-full bg-gradient-to-r group-hover:from-transparent group-hover:to-primary/15 absolute duration-200 transition-[width] right-0 top-0" />
          <div className="shrink-0 w-10 h-10 rounded-lg bg-gradient-to-b from-white/10 to-primary/10 flex items-center justify-center drop-shadow-primary-2px hover:drop-shadow-primary-8px duration-200">
            <AndroidIcon className="h-4 text-primary" />
          </div>
          <div>
            <div className="text-sm">Android</div>
            <div className="text-xs text-white/60 -mt-1.5">
              Available on Google Play
            </div>
          </div>
          <div className="ml-auto pr-2 group-hover:text-primary duration-200 group-hover:-mr-1">
            <ChevronRight className="w-4 h-4" />
          </div>
        </Link>
      </div>
    ),
  },
  {
    name: LinkEnum.CARDS,
    href: "#",
    description:
      "Spend crypto at 70+ million merchants worldwide with competitive fees.",
    subLinks: [
      {
        name: LinkEnum.ALL_CARDS,
        href: "https://basedapp.io/cards",
        icon: <CreditCard className="w-4 h-4 text-primary" />,
        description: "Compare our card offerings",
      },
      {
        name: LinkEnum.GOLD,
        href: "https://basedapp.io/gold",
        icon: <Spade className="w-4 h-4 text-primary" />,
        description: "Premium card solution",
      },
    ],
  },
  {
    name: LinkEnum.AFFILIATE,
    href: "#",
    description:
      "Invite your friends to trade, and earn up to 36% of their trading fees.",
    subLinks: [
      {
        name: LinkEnum.AFFILIATE_LANDING,
        href: "https://basedapp.io/affiliate",
        icon: <MessageCircle className="w-4 h-4 text-primary" />,
        description: "Understand our affiliate program",
      },
      {
        name: LinkEnum.AFFILIATE_PORTAL,
        href: "https://basedapp.io/affiliate/portal",
        icon: <Circle className="w-4 h-4 text-primary" />,
        description: "Manage your affiliate account",
      },
    ],
  },
  {
    name: LinkEnum.RESOURCES,
    href: "#",
    description: "All our other content for your perusal.",
    panelVariant: PanelVariant.EQUAL_SPLIT,
    subLinks: [
      {
        name: LinkEnum.SECURITY,
        href: "https://basedapp.io/security",
        icon: <ShieldCheck className="w-4 h-4 text-primary" />,
      },
      {
        name: LinkEnum.BLOG,
        href: "/",
        icon: <Book className="w-4 h-4 text-primary" />,
      },
      {
        name: LinkEnum.FAQ,
        href: "https://basedapp.io/faq",
        icon: <HelpCircle className="w-4 h-4 text-primary" />,
      },
    ],
  },
];

const getSlug = (name: string) => {
  return name.replace(" ", "-").toLowerCase();
};

export const NavBar = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showSublink, setShowSublink] = useState<LinkEnum | undefined>();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > SCROLL_HEIGHT) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const showSublinkContainer = useCallback(() => {
    const containerElem = document.getElementById("panel");
    if (containerElem) {
      containerElem.style.display = "block";
      containerElem.style.opacity = "1";
    }
  }, []);

  const hideSublinkContainer = (e: MouseEvent) => {
    const sublinkParents = document.getElementsByClassName("sublink-parent");
    const containerElem = document.getElementById("panel");

    const inContainer = containerElem?.contains(e.target as Node);
    const inAnySublink = Array.from(sublinkParents).some((sublink) =>
      sublink.contains(e.target as Node)
    );
    const inEither = inContainer || inAnySublink;

    if (!inEither && containerElem) {
      containerElem.style.opacity = "0";
      containerElem.style.display = "none";
      setShowSublink(undefined);
    }
  };

  useEffect(() => {
    if (showSublink) {
      window.addEventListener("mousemove", hideSublinkContainer);
    }
    return () => {
      window.removeEventListener("mousemove", hideSublinkContainer);
    };
  }, [showSublink]);

  const sublinkData = useMemo(() => {
    if (!showSublink) {
      return undefined;
    }
    return LINKS.find((link) => link.name === showSublink);
  }, [showSublink]);

  return (
    <div className="sticky top-0 z-[100]">
      <div className="relative">
        <nav className="h-[64px] flex justify-center items-center z-[200] absolute left-1/2 -translate-x-1/2 w-full lg:w-auto px-3 lg:px-0 top-0">
          <div
            className={cn(
              "flex items-center font-display border border-white/15 rounded-full py-1.5 bg-[#131313] text-sm px-4 lg:px-2 w-full"
            )}>
            <Link
              to="/"
              className="h-5 aspect-[1417.21/1603.72] shrink-0 sm:hidden">
              <LogoIcon variant={LogoVariant.DEFAULT} />
            </Link>
            <Link
              to="/"
              className="h-5 aspect-[1146/186.513477] shrink-0 hidden sm:block lg:hidden">
              <Logo variant={LogoVariant.DEFAULT} />
            </Link>
            <div className="flex items-center ml-auto">
              {LINKS.map((link) => {
                const hasSublinks = !!link.subLinks && link.subLinks.length > 0;
                const isActive =
                  window.location.pathname === link.href ||
                  (hasSublinks && showSublink === link.name);
                const isExternal = link.href.startsWith("http");
                if (isExternal) {
                  return (
                    <a
                      key={getSlug(link.name)}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "cursor-pointer px-4 py-1.5 rounded-full border border-transparent text-white duration-200 hidden lg:block text-xs leading-[0.813rem]",
                        isActive
                          ? "bg-white/5 text-white/40"
                          : "hover:bg-primary/5 hover:border-primary/15 hover:text-primary"
                      )}>
                      {link.name}
                    </a>
                  );
                }
                if (hasSublinks) {
                  return (
                    <div
                      id={getSlug(link.name)}
                      key={getSlug(link.name)}
                      onMouseEnter={() => {
                        if (hasSublinks) {
                          setShowSublink(link.name as LinkEnum);
                          showSublinkContainer();
                        }
                      }}
                      className={cn(
                        "sublink-parent cursor-pointer px-4 py-1.5 rounded-full border border-transparent text-white duration-200 hidden lg:block text-xs leading-[0.813rem]",
                        isActive
                          ? "bg-primary/5 border-primary/15 text-primary"
                          : "hover:bg-primary/5 hover:border-primary/15 hover:text-primary"
                      )}>
                      {link.name}
                    </div>
                  );
                }
                return (
                  <Link
                    id={getSlug(link.name)}
                    type="button"
                    key={getSlug(link.name)}
                    className={cn(
                      "px-4 py-1.5 rounded-full border border-transparent text-white duration-200 hidden lg:block text-xs leading-[0.813rem]",
                      isActive
                        ? "cursor-default text-white/40"
                        : "cursor-pointer hover:bg-primary/5 hover:border-primary/15 hover:text-primary"
                    )}
                    to={link.href}>
                    {link.name}
                  </Link>
                );
              })}
            </div>
            <div id="navbar-cta-container" className="">
              <NavBarConnect
                className={cn(
                  "shrink-0 whitespace-nowrap mr-1 lg:mr-0 ",
                  scrolled
                    ? "opacity-100 ml-1 pl-4 lg:border-l lg:border-white/10"
                    : "opacity-100 lg:w-[0px] lg:opacity-0"
                )}
              />
            </div>
            <Hamburger isOpen={isOpen} setIsOpen={setIsOpen} />
          </div>
        </nav>
        <div
          className={cn(
            "h-screen w-screen lg:hidden fixed top-0 left-0 bg-black text-white z-[199] px-4 pt-[64px] duration-200 flex items-center overflow-hidden",
            isOpen ? "visible opacity-100" : "invisible opacity-0"
          )}>
          <div className="flex flex-col w-full h-full">
            {LINKS.map((link) => {
              const hasSublinks = !!link.subLinks && link.subLinks.length > 0;
              if (hasSublinks) {
                return (
                  <div
                    id={getSlug(link.name)}
                    key={getSlug(link.name)}
                    className="cursor-default border-b border-white/10">
                    <div className="px-4 pt-4 flex items-center w-full text-xxs uppercase tracking-widest text-primary drop-shadow-primary-3px">
                      {link.name}
                    </div>
                    {link?.subLinks?.map((sublink) => (
                      <div
                        key={getSlug(sublink.name)}
                        onClick={() => {
                          if (!sublink.comingSoon) {
                            navigate(sublink.href);
                            setIsOpen(false);
                          }
                        }}
                        className="px-4 py-4 cursor-pointer hover:bg-white/[0.075] hover:text-primary duration-200 flex items-center text-sm">
                        <div>{sublink.name}</div>
                        {sublink.comingSoon ? (
                          <div className="ml-auto text-sm text-white/60">
                            COMING SOON
                          </div>
                        ) : (
                          <div className="ml-auto">
                            <ArrowRightIcon className="w-4 h-4 group-hover:text-primary" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              }
              return (
                <div
                  key={getSlug(link.name)}
                  className="cursor-pointer px-4 py-4 hover:bg-white/[0.075] text-sm hover:text-primary duration-200  border-b border-white/10 flex items-center group"
                  onClick={() => {
                    navigate(link.href);
                    setIsOpen(false);
                  }}>
                  <div>{link.name}</div>
                  <div className="ml-auto">
                    <ArrowRightIcon className="w-4 h-4 group-hover:text-primary" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div
          id="panel"
          className="max-w-2xl w-full fixed left-1/2 -translate-x-1/2 top-[calc(53px_-_0.5rem_-_1px)] z-[201] pt-4"
          style={{
            display: "none",
          }}>
          <div className="bg-card p-2 rounded-3xl w-full h-full relative isolate overflow-hidden">
            <svg
              viewBox="0 0 1024 1024"
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 -z-10 size-[64rem] -translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] sm:left-full sm:-ml-80 lg:left-1/2 lg:ml-0 lg:-translate-x-1/2 lg:translate-y-0">
              <circle
                r={512}
                cx={512}
                cy={512}
                fill={`url(#panelGradient)`}
                fillOpacity="0.7"
              />
              <defs>
                <radialGradient id="panelGradient">
                  <stop stopColor="#ff6b42" />
                  <stop offset={1} stopColor="#ff6b42" />
                </radialGradient>
              </defs>
            </svg>
            <div
              className={cn(
                "rounded-2xl bg-black/75 grid",
                sublinkData?.panelVariant === PanelVariant.EQUAL_SPLIT
                  ? "grid-cols-2"
                  : "grid-cols-5"
              )}>
              {sublinkData?.leftPanel ? (
                sublinkData.leftPanel
              ) : (
                <div
                  className={cn(
                    "p-8 border-r border-white/10",
                    sublinkData?.panelVariant === PanelVariant.EQUAL_SPLIT
                      ? "col-span-1"
                      : "col-span-2"
                  )}>
                  <div className="text-xl">{sublinkData?.name}</div>
                  <div className="mt-3 text-white/60 font-light text-sm">
                    {sublinkData?.description}
                  </div>
                </div>
              )}
              <div
                className={cn(
                  "px-4 flex flex-col gap-y-2 pt-4 pb-6",
                  sublinkData?.panelVariant === PanelVariant.EQUAL_SPLIT
                    ? "col-span-1"
                    : "col-span-3"
                )}>
                <div className="px-2 pt-2 font-display text-xs uppercase tracking-widest mt-1 text-primary drop-shadow-primary-3px">
                  {sublinkData?.subLinksTitle ?? ""}
                </div>
                {sublinkData?.subLinks?.map((sublink) => {
                  const isExternal = sublink.href.startsWith("http");
                  return sublink.comingSoon ? (
                    <div
                      key={getSlug(sublink.name)}
                      className="group flex items-center gap-x-4 overflow-hidden rounded-xl py-2 px-2 relative isolate">
                      <div className="w-[0px] group-hover:w-full h-full bg-gradient-to-r group-hover:from-transparent group-hover:to-primary/15 absolute duration-200 transition-[width] right-0 top-0" />
                      <div className="shrink-0 w-10 h-10 rounded-lg bg-gradient-to-b from-white/10 to-primary/10 flex items-center justify-center drop-shadow-primary-2px hover:drop-shadow-primary-8px duration-200">
                        {sublink.icon}
                      </div>
                      <div>
                        {sublink?.comingSoon && (
                          <div className="text-sm text-white/50">
                            COMING SOON
                          </div>
                        )}
                        <div className="text-sm">{sublink.name}</div>
                      </div>
                      {!sublink?.comingSoon && (
                        <div className="ml-auto pr-2 group-hover:text-primary duration-200">
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={sublink.href}
                      key={getSlug(sublink.name)}
                      {...(isExternal
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      className="group cursor-pointer flex items-center gap-x-4 overflow-hidden rounded-xl py-2 px-2 relative isolate">
                      <div className="w-[0px] group-hover:w-full h-full bg-gradient-to-r group-hover:from-transparent group-hover:to-primary/15 absolute duration-200 transition-[width] right-0 top-0" />
                      <div className="shrink-0 w-10 h-10 rounded-lg bg-gradient-to-b from-white/10 to-primary/10 flex items-center justify-center drop-shadow-primary-2px hover:drop-shadow-primary-8px duration-200">
                        {sublink.icon}
                      </div>
                      <div>
                        <div className="text-sm">{sublink.name}</div>
                        {sublink?.description && (
                          <div className="text-xs text-white/60 -mt-1.5">
                            {sublink.description}
                          </div>
                        )}
                      </div>
                      <div className="ml-auto pr-2 group-hover:text-primary duration-200 group-hover:-mr-1">
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export function Hamburger({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) {
  return (
    <button
      className="relative w-4 h-4 flex flex-col justify-between ml-2 lg:hidden"
      onClick={() => setIsOpen(!isOpen)}>
      <motion.span
        className="block w-4 h-[1.5px] bg-white"
        animate={{
          rotate: isOpen ? 45 : 0,
          y: isOpen ? 7 : 0,
        }}
        transition={{ duration: 0.3 }}
      />
      <motion.span
        className="block w-4 h-[1.5px] bg-white"
        animate={{
          opacity: isOpen ? 0 : 1,
        }}
        transition={{ duration: 0.3 }}
      />
      <motion.span
        className="block w-4 h-[1.5px] bg-white"
        animate={{
          rotate: isOpen ? -45 : 0,
          y: isOpen ? -7 : 0,
        }}
        transition={{ duration: 0.3 }}
      />
    </button>
  );
}
